/* eslint-disable max-lines */
import {CSSProperties, FC, useCallback} from "react";
import ReactSelect, {
  components,
  StylesConfig,
  MultiValue,
  ActionMeta,
  MultiValueProps,
  ControlProps,
  OptionProps,
  DropdownIndicatorProps,
} from "react-select";
import Checkbox from "./checkbox";
import {useTranslation} from "react-i18next";
import cn from "classnames";
import classes from "./MultiSelectField.module.css";
import {formatToMoscowDate} from "../time";
import Scroll from "./scroll";

export type ChallengeBase = {
  id?: string;
  name?: string;
  value?: string;
  alreadyAssigned?: boolean;
  activateAt?: number;
  deactivateAt?: number;
};

export type ChallengeOption = ChallengeBase & {
  value?: string;
  label?: string;
  checked?: boolean;
};

export type ChallengeParticipants = {
  id?: string;
  challengeId?: string;
  activateAt?: number;
  deactivateAt?: number;
};

interface CustomMultiValueProps extends MultiValueProps<ChallengeOption> {
  data: ChallengeOption;
}

export interface IMultiSelectFieldProps {
  label?: string;
  name: string;
  onChange: (data: {
    name: string;
    value: MultiValue<ChallengeOption>;
    checkedOptions: ChallengeParticipants[];
  }) => void;
  defaultValue: Array<ChallengeOption>;
  options: Array<ChallengeOption> | Record<string, ChallengeOption>;

  isFixed?: boolean;
  toTop?: boolean;
  checkedOptions?: ChallengeParticipants[];
  setCheckedOptions: (data: ChallengeParticipants[]) => void;
  setActivateAt: (date?: Date) => void;
  setDeactivateAt: (date?: Date) => void;
}

const MultiSelectField: FC<IMultiSelectFieldProps> = ({
  label,
  options,
  onChange,
  name,
  defaultValue,
  toTop,
  checkedOptions,
  setCheckedOptions,
  setActivateAt,
  setDeactivateAt,
}) => {
  const {t, i18n} = useTranslation();

  const optionsArray =
    !Array.isArray(options) && typeof options === "object"
      ? Object.keys(options).map((optionName) => ({
          label: options[optionName].name,
          value: options[optionName].id,
          alreadyAssigned: options[optionName].alreadyAssigned,
          activateAt: options[optionName].activateAt,
          deactivateAt: options[optionName].deactivateAt,
          checked: false,
        }))
      : options;

  const handleChange = useCallback(
    (value: MultiValue<ChallengeOption>, meta: ActionMeta<ChallengeOption>) => {
      if (meta.action === "clear") {
        setCheckedOptions([]);
        onChange({name, value: [], checkedOptions: []});
        return;
      }

      const newCheckedData = value
        .filter((item) => item.checked)
        .map((item) => ({
          id: item.value,
          name: item.label,
          activateAt: item.activateAt,
          deactivateAt: item.deactivateAt,
        }));

      setCheckedOptions(newCheckedData);
      onChange({name, value, checkedOptions: newCheckedData});
    },
    [name, onChange, setCheckedOptions],
  );

  const checkedIds = new Set(checkedOptions?.map((item) => item.id) || []);

  const optionsArrayWithChecked = optionsArray.map((opt) => ({
    ...opt,
    checked: checkedIds.has(opt.value),
  }));

  const defaultValueWithChecked = Array.isArray(defaultValue)
    ? defaultValue.map((opt) => ({
        ...opt,
        checked: checkedIds.has(opt.value),
      }))
    : [];

  const getInputClasses = () => {
    return "select multi-select-field__input";
  };

  const CustomOption: FC<OptionProps<ChallengeOption, true>> = ({innerProps, isDisabled, ...rest}) => {
    return !isDisabled ? (
      <Scroll>
        <div {...innerProps} className={classes.optionItem}>
          <span>
            {rest.data.label}{" "}
            {rest.data.alreadyAssigned ? `(${t("components.AssignModal.warning.alreadyAssigned")})` : ""}
          </span>
        </div>
      </Scroll>
    ) : null;
  };

  const CustomMultiValue: FC<CustomMultiValueProps> = (props) => {
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const onValueClick = (e: React.MouseEvent<HTMLSpanElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!(e.target as HTMLElement).closest("input[type='checkbox']")) {
        if (props.selectProps.menuIsOpen) {
          props.selectProps.onMenuClose();
        } else {
          props.selectProps.onMenuOpen();
        }
      }
    };

    const onCheckboxClick = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const newValue = (props.selectProps.value as ChallengeOption[])?.map((item) => {
        if (item.value === props.data.value) {
          return {...item, checked: !item.checked};
        }
        return item;
      });
      setActivateAt(undefined);
      setDeactivateAt(undefined);

      props.selectProps.onChange(newValue, {action: "select-option", option: props.data});
    };

    const innerProps = {...props.innerProps, onMouseDown: handleMouseDown};
    const dateContent = props.data.alreadyAssigned
      ? ` (${t("components.AssignModal.warning.alreadyAssigned")}) ${
          props.data.activateAt ? t("components.AssignModal.warning.from") : ""
        } ${props.data.activateAt ? formatToMoscowDate(props.data.activateAt, i18n.language) : ""}
                ${props.data.deactivateAt ? t("components.AssignModal.warning.to") : ""}
              ${props.data.deactivateAt ? formatToMoscowDate(props.data.deactivateAt, i18n.language) : ""}`
      : props.data.activateAt || props.data.deactivateAt
        ? ` (${t("components.AssignModal.warning.from")}
                ${
                  !props.data.activateAt
                    ? t("components.AssignModal.datePicker.activeFrom")
                    : formatToMoscowDate(props.data.activateAt, i18n.language)
                }
                ${t("components.AssignModal.warning.to")} ${
                  !props.data.deactivateAt ? "∞" : formatToMoscowDate(props.data.deactivateAt, i18n.language)
                })`
        : "";

    return (
      <components.MultiValue
        {...props}
        innerProps={innerProps}
        className={cn(classes.inputItem, props.data.alreadyAssigned ? classes.inputItemDisabled : "")}
      >
        <Checkbox
          checked={props.data.checked}
          disabled={props.data.alreadyAssigned}
          onChange={onCheckboxClick}
          data-id={props.data.value}
          crossed={props.data.alreadyAssigned}
        />
        <div className={dateContent.length > 35 ? classes.itemDropdownLong : classes.itemDropdownShort}>
          <span
            style={{
              fontWeight: "bold",
              marginLeft: 7,
              paddingRight: 7,
              cursor: props.data.alreadyAssigned ? "default" : "pointer",
            }}
            className={dateContent.length > 35 ? classes.scrollableText : ""}
            onClick={onValueClick}
          >
            <span className={classes.scrollableTextInner}>
              {props.data.label}
              {dateContent}
            </span>
          </span>
        </div>
      </components.MultiValue>
    );
  };

  const colourStyles: StylesConfig<ChallengeBase, true> = {
    valueContainer: (styles) => ({
      ...styles,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      overflowY: "auto",
      paddingTop: "7px",
      marginLeft: "10px",
      cursor: "pointer",
    }),
    control: (styles) => ({...styles, backgroundColor: "white", borderRadius: "25px", cursor: "pointer"}),
    option: (styles, {data, isDisabled, isFocused, isSelected}) => {
      return {
        ...styles,
        backgroundColor: "#000",
        cursor: isDisabled ? "not-allowed" : "default",
        ":hover": {
          ...styles[":hover"],
          backgroundColor: "red",
          cursor: "pointer",
        },
      };
    },
    multiValue: (styles, {data}) => {
      let style = {
        ...styles,
        display: "flex",
        justifyContent: "space-between",
        width: "calc(100% - 7px)",
        backgroundColor: "#f4f4fca6",
        borderRadius: "7px",
        paddingTop: "3px",
        paddingBottom: "3px",
        paddingRight: "7px",
        cursor: "pointer",
      };
      if (data.alreadyAssigned) {
        style.backgroundColor = "#f4f4fca6";
        style.cursor = "default";
        style.width = "calc(100% - 7px)";
        return style;
      }
      return style;
    },
    multiValueLabel: (styles, {data}) => ({
      ...styles,
      width: "100%",
    }),
    multiValueRemove: (styles, {data}) => ({
      ...styles,
      ":hover": {
        backgroundColor: "#ee498e",
        color: "#fff",
        borderRadius: "5px",
        cursor: "pointer",
      },
    }),
  };

  const DropdownIndicator = (props: DropdownIndicatorProps<ChallengeOption, true>) => {
    const {
      selectProps: {menuIsOpen},
    } = props;

    const style: CSSProperties = {
      transform: menuIsOpen ? "rotate(90deg)" : "rotate(0deg)",
      transition: "transform 0.3s ease",
    };

    return (
      <components.DropdownIndicator {...props}>
        <svg style={style} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M7 5L13 10L7 15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </components.DropdownIndicator>
    );
  };

  return (
    <div>
      <label className="multi-select-field__label-select">{label}</label>
      <ReactSelect
        isMulti
        options={optionsArrayWithChecked}
        className={getInputClasses()}
        classNamePrefix="react-select"
        onChange={handleChange}
        name={name}
        defaultValue={defaultValue}
        value={defaultValueWithChecked}
        menuPlacement={toTop ? "top" : "bottom"}
        components={{
          Option: CustomOption,
          MultiValue: CustomMultiValue,
          Control: ({children, ...props}: ControlProps<ChallengeOption, true>) => (
            <components.Control {...props}>{children}</components.Control>
          ),
          DropdownIndicator,
        }}
        styles={colourStyles}
        closeMenuOnSelect={false}
        isClearable
        placeholder={t("components.MultiSelectField.placeholder")}
        noOptionsMessage={() => t("components.MultiSelectField.noOptions")}
      />
    </div>
  );
};

export default MultiSelectField;
